// src/types/scheduleItem.ts
import { TimeBox } from './timeBox';
import { Event } from './calendar-event';
import { format } from 'date-fns';

// 統一的排程項目類型
export type ScheduleItem = TimeBox | Event;

// 類型判斷函數 - 基於明確的 itemType 字段
export function isTimeBox(item: ScheduleItem): item is TimeBox {
  return item.itemType === 'timebox';
}

export function isEvent(item: ScheduleItem): item is Event {
  return item.itemType === 'event';
}

// 帶有顯示元數據的排程項目
export interface ScheduleItemWithMeta {
  displayType: 'timebox' | 'event';
  isEditable: boolean;
  visualTheme: 'default' | 'event';
}

// TimeBox 帶有顯示元數據
export interface TimeBoxWithMeta extends TimeBox, ScheduleItemWithMeta {}

// Event 帶有顯示元數據
export interface EventWithMeta extends Event, ScheduleItemWithMeta {}

// 更強化的型別守衛函數 - 檢查最具體的屬性
export function isTimeBoxStrict(item: any): item is TimeBox {
  return (
    typeof item === 'object' &&
    item !== null &&
    item.itemType === 'timebox' &&
    'category' in item &&
    !('timeType' in item) &&
    typeof item.id === 'string' &&
    typeof item.title === 'string' &&
    item.startTime instanceof Date &&
    item.endTime instanceof Date
  );
}

export function isEventStrict(item: any): item is Event {
  return (
    typeof item === 'object' &&
    item !== null &&
    item.itemType === 'event' &&
    'timeType' in item &&
    !('category' in item && !('timeType' in item)) && // 確保不是 TimeBox
    typeof item.id === 'string' &&
    typeof item.title === 'string' &&
    item.startTime instanceof Date
  );
}

// 安全的類型轉換函數
export function asTimeBox(item: ScheduleItem): TimeBox | null {
  return isTimeBoxStrict(item) ? item : null;
}

export function asEvent(item: ScheduleItem): Event | null {
  return isEventStrict(item) ? item : null;
}

// 複合型別判斷
export function getScheduleItemType(item: ScheduleItem): 'timebox' | 'event' | 'unknown' {
  if (isTimeBox(item)) return 'timebox';
  if (isEvent(item)) return 'event';
  return 'unknown';
}

// 安全的屬性存取函數
export function getScheduleItemColor(item: ScheduleItem): string {
  if (isTimeBox(item)) {
    switch (item.category) {
      case 'focus': return 'blue';
      case 'routine': return 'slate';
      case 'life': return 'green';
      case 'rest': return 'amber';
      default: return 'gray';
    }
  }
  if (isEvent(item)) {
    return 'red';
  }
  return 'gray';
}

export function isScheduleItemEditable(item: ScheduleItem): boolean {
  // TimeBox 一般都可編輯
  if (isTimeBox(item)) {
    return true;
  }
  
  // Event 的可編輯性取決於來源
  if (isEvent(item)) {
    return item.source === 'manual' || !item.source;
  }
  
  return false;
}

// 獲取顯示用的時間範圍
export function getScheduleItemTimeRange(item: ScheduleItem): string {
  const startTime = item.startTime;
  const endTime = item.endTime;
  
  if (!endTime) {
    return format(startTime, 'HH:mm');
  }
  
  return `${format(startTime, 'HH:mm')}-${format(endTime, 'HH:mm')}`;
}